import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly DARK_MODE_KEY = 'dark-mode';
  private isDarkMode = false;
  private isBrowser: boolean;
  private darkModeSubject = new BehaviorSubject<boolean>(false);
  public darkMode$ = this.darkModeSubject.asObservable();

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    if (this.isBrowser) {
      this.loadTheme();
    }
  }

  private loadTheme(): void {
    if (!this.isBrowser) return;
    
    try {
      const savedTheme = localStorage.getItem(this.DARK_MODE_KEY);
      this.isDarkMode = savedTheme === 'true';
      this.darkModeSubject.next(this.isDarkMode);
      this.applyTheme();
    } catch (error) {
      console.warn('Failed to load theme from localStorage:', error);
    }
  }

  private applyTheme(): void {
    if (!this.isBrowser) return;
    
    try {
      const htmlElement = document.querySelector('html');
      if (htmlElement) {
        if (this.isDarkMode) {
          htmlElement.classList.add('dark-mode');
        } else {
          htmlElement.classList.remove('dark-mode');
        }
      }
    } catch (error) {
      console.warn('Failed to apply theme:', error);
    }
  }

  toggleDarkMode(): void {
    this.isDarkMode = !this.isDarkMode;
    this.darkModeSubject.next(this.isDarkMode);
    this.saveTheme();
    this.applyTheme();
  }

  private saveTheme(): void {
    if (!this.isBrowser) return;
    
    try {
      localStorage.setItem(this.DARK_MODE_KEY, this.isDarkMode.toString());
    } catch (error) {
      console.warn('Failed to save theme to localStorage:', error);
    }
  }

  getDarkMode(): boolean {
    return this.isDarkMode;
  }

  setDarkMode(enabled: boolean): void {
    this.isDarkMode = enabled;
    this.darkModeSubject.next(this.isDarkMode);
    this.saveTheme();
    this.applyTheme();
  }
}
